import * as React from 'react';
import { Bug, Check } from 'lucide-react';

import { cn } from '@common/lib/utils';
import { Badge } from '@common/components/ui/badge';
import { Button } from '@common/components/button';
import { DialogWrapper } from '@common/components/modal/dialog-wrapper';
import {
  API_BASE_URL_OVERRIDE_KEY,
  BASE_URL,
  DEFAULT_BASE_URL,
  IS_DEBUG_API_OVERRIDE_ENABLED,
} from '@common/constants';

// Same-origin proxy on the dashboard server. It holds INTERNAL_TOKEN_SECRET and
// forwards to the node-api's internal registry, so the secret never reaches the
// browser (see source/server/preview-environments-proxy.js).
const PREVIEW_ENVIRONMENTS_ENDPOINT = '/api/preview-environments';

type PreviewEnvironment = {
  label: string;
  url: string;
  kind: string;
  pull_request_id?: number;
  commit?: string;
  commit_message?: string;
  live?: boolean;
};

type Status = 'idle' | 'loading' | 'error';

const normalizeUrl = (url: string) => url.replace(/\/+$/, '');

// BASE_URL and its consumers capture the host at module-init, so switching the
// override has to reload the page for everything to re-resolve.
const applyOverride = (url: string | null) => {
  if (url) {
    window.localStorage.setItem(API_BASE_URL_OVERRIDE_KEY, url);
  } else {
    window.localStorage.removeItem(API_BASE_URL_OVERRIDE_KEY);
  }
  window.location.reload();
};

export const DebugMenu = () => {
  const [open, setOpen] = React.useState(false);
  const [status, setStatus] = React.useState<Status>('idle');
  const [environments, setEnvironments] = React.useState<PreviewEnvironment[]>([]);

  const activeUrl = normalizeUrl(BASE_URL);
  const isOverridden = activeUrl !== normalizeUrl(DEFAULT_BASE_URL);

  const fetchEnvironments = React.useCallback(async () => {
    setStatus('loading');
    try {
      const response = await fetch(PREVIEW_ENVIRONMENTS_ENDPOINT, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const body = await response.json();
      setEnvironments(Array.isArray(body?.data) ? body.data : []);
      setStatus('idle');
    } catch {
      setStatus('error');
    }
  }, []);

  React.useEffect(() => {
    if (open) fetchEnvironments();
  }, [open, fetchEnvironments]);

  // Defensive: only ever render on non-production builds. This is placed after
  // the hooks so the rules-of-hooks contract holds regardless.
  if (!IS_DEBUG_API_OVERRIDE_ENABLED) return null;

  const title = (
    <span className="tw-flex tw-items-center tw-gap-2">
      <span
        className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-md tw-bg-orange-light tw-text-orange-custom"
      >
        <Bug size={14} />
      </span>
      API environment
    </span>
  );

  const content = (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-text-xs tw-text-gray-custom-500">
        Active:{' '}
        <span className="tw-font-mono tw-text-gray-custom-700">{activeUrl}</span>
      </div>

      {status === 'loading' && (
        <p className="tw-py-8 tw-text-center tw-text-sm tw-text-gray-custom-500">
          Loading environments…
        </p>
      )}

      {status === 'error' && (
        <div className="tw-py-8 tw-text-center tw-text-sm tw-text-red-link">
          Could not load environments.{' '}
          <button type="button" onClick={fetchEnvironments} className="tw-underline">
            Retry
          </button>
        </div>
      )}

      {status === 'idle' && (
        <div className="tw-flex tw-flex-col tw-gap-1">
          {environments.map((env) => {
            const isActive = normalizeUrl(env.url) === activeUrl;

            return (
              <button
                type="button"
                key={env.url}
                onClick={() => applyOverride(normalizeUrl(env.url))}
                className={cn(
                  'tw-flex tw-w-full tw-flex-col tw-gap-1 tw-rounded-lg tw-border tw-p-3',
                  'tw-text-left tw-transition-colors tw-bg-white tw-cursor-pointer',
                  isActive
                    ? 'tw-border-gray-700'
                    : 'tw-border-gray-200 hover:tw-bg-gray-custom-100',
                )}
              >
                <span className="tw-flex tw-items-center tw-gap-2">
                  {isActive && (
                    <Check size={14} className="tw-shrink-0 tw-text-orange-custom" />
                  )}
                  <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-font-medium">
                    {env.label}
                  </span>
                  <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                    <Badge variant={env.kind === 'testing' ? 'beta' : 'new'} size="sm">
                      {env.kind}
                    </Badge>
                    {env.live === false && (
                      <Badge variant="paused" size="sm">
                        offline
                      </Badge>
                    )}
                  </span>
                </span>
                <span className="tw-truncate tw-font-mono tw-text-xs tw-leading-5 tw-text-gray-custom-500">
                  {env.url}
                </span>
                {env.commit_message && (
                  <span className="tw-truncate tw-text-xs tw-leading-5 tw-text-gray-custom-400">
                    {env.pull_request_id ? `#${env.pull_request_id} · ` : ''}
                    {env.commit ? `${env.commit} · ` : ''}
                    {env.commit_message}
                  </span>
                )}
              </button>
            );
          })}

          {environments.length === 0 && (
            <div className="tw-py-8 tw-text-center tw-text-sm tw-text-gray-custom-500">
              No environments found.
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <button
        type="button"
        aria-label="Open API debug menu"
        title="API debug menu"
        onClick={() => setOpen(true)}
        className={cn(
          'tw-fixed tw-bottom-4 tw-left-4 tw-z-[9999] tw-flex tw-h-10 tw-w-10',
          'tw-items-center tw-justify-center tw-rounded-full tw-bg-orange-custom',
          'tw-text-white tw-shadow-lg tw-transition-transform hover:tw-scale-105',
        )}
      >
        <Bug size={18} />
        {isOverridden && (
          <span
            className={cn(
              'tw-absolute tw-right-0 tw-top-0 tw-h-2.5 tw-w-2.5',
              'tw-rounded-full tw-bg-white tw-ring-2 tw-ring-orange-custom',
            )}
          />
        )}
      </button>

      <DialogWrapper
        open={open}
        onOpenChange={setOpen}
        size="sm"
        title={title}
        description="Point the dashboard at a preview API. Selecting an environment reloads the page."
        content={content}
        hideFooter={!isOverridden}
        customFooter={
          isOverridden ? (
            <Button
              type="default"
              size="small"
              icon="undo"
              onClick={() => applyOverride(null)}
            >
              Reset to default
            </Button>
          ) : undefined
        }
      />
    </>
  );
};
